import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    AddingCard,
    CallToActionNullState,
    Dialog,
    DialogContent,
    Loading,
} from '../../../compositions';
import { closeDialog, openDialog } from '../../../helpers/dialog';
import { userHasRole } from '../../../helpers/role';
import trans from '../../../helpers/trans';
import { Skill, SkillBase, SkillsFormData } from '../../../models/@competentNL/Skills/Skills';
import { JobExperience, JobExperienceFormData } from '../../../models/JobExperience/JobExperience';
import { FormOption, UserRole } from '../../../types';
import { JobExperienceForm, SkillsForm } from '../..';
import { JobExperienceItem } from './subcomponents';

import './JobExperiencesCard.scss';

interface JobExperiencesCardProps {
    isLoading?: boolean;
    jobExperienceIsLoading: boolean;
    jobExperienceCreateIsSuccessful: boolean;
    jobExperienceUpdateIsSuccessful: boolean;
    skillsIsLoading: boolean;
    skillsStepIsSuccessful: boolean;
    hasError?: boolean;
    jobExperienceError: string;
    skillOptionsError: string;
    userRoles?: UserRole[];
    jobExperiences: JobExperience[];
    skills: Skill[];
    occupationOptions: FormOption[];
    skillOptions: SkillBase[];
    searchedSkillOptions: SkillBase[];
    onOccupationInput: (query: string) => void;
    onSelectOccupation: (value: string) => void;
    onAddJobExperience: (jobExperienceFormData: JobExperienceFormData) => void;
    onEditJobExperience: (jobExperienceFormData: JobExperienceFormData) => void;
    onDeleteJobExperience: (jobExperience: JobExperience) => void;
    onSearchSkills: (query: string) => void;
    onAddSkills: (skillsFormData: SkillsFormData) => void;
    onIsSuccessfulReset: () => void;
    className?: string;
}

const JobExperiencesCard: FC<JobExperiencesCardProps> = ({
    isLoading,
    jobExperienceIsLoading,
    jobExperienceCreateIsSuccessful,
    jobExperienceUpdateIsSuccessful,
    skillsIsLoading,
    skillsStepIsSuccessful,
    hasError,
    jobExperienceError,
    skillOptionsError,
    userRoles = [],
    jobExperiences,
    skills,
    occupationOptions,
    skillOptions,
    searchedSkillOptions,
    onOccupationInput,
    onSelectOccupation,
    onAddJobExperience,
    onEditJobExperience,
    onDeleteJobExperience,
    onSearchSkills,
    onAddSkills,
    onIsSuccessfulReset,
    className = '',
}): ReactElement => {
    const dialogRef = useRef<HTMLDialogElement>(null);

    const numberOfFormSteps = 2;

    const [formStep, setFormStep] = useState<number>(1);
    const [jobExperienceFormKey, setJobExperienceFormKey] = useState<number>(0);
    const [occupation, setOccupation] = useState<string>('');

    const dialogStep = useMemo((): string => (
        formStep === 2 ? 'skills' : 'jobExperience'
    ), [formStep]);

    const handleOpenDialog = (): void => openDialog(dialogRef);
    const handleCloseDialog = (): void => {
        closeDialog(dialogRef);

        setFormStep(1);
        setJobExperienceFormKey(jobExperienceFormKey + 1);
    };

    const handleAddJobExperience = (jobExperienceFormData: JobExperienceFormData): void => {
        setOccupation(jobExperienceFormData.position);

        onAddJobExperience(jobExperienceFormData);
    };

    useEffect((): void => {
        if (jobExperienceCreateIsSuccessful) {
            setFormStep(2);
        }
    }, [jobExperienceCreateIsSuccessful]);

    useEffect((): void => {
        if (skillsStepIsSuccessful) {
            handleCloseDialog();
            onIsSuccessfulReset();
        }
    }, [skillsStepIsSuccessful]);

    return (
        <AddingCard
            isEditable={userHasRole(UserRole.candidate, userRoles) && jobExperiences.length > 0}
            showFillLabel={jobExperiences.length === 0 && !isLoading}
            title={trans('containers.jobExperiencesCard.title')}
            item={trans('common.jobExperience')}
            userRoles={userRoles}
            onAddClick={handleOpenDialog}
            className={`job-experiences-card ${className}`}
        >
            {isLoading && <Loading className="job-experiences-card__loader" />}

            {!isLoading && jobExperiences.length === 0 && (
                <CallToActionNullState
                    isEditable={!hasError && userHasRole(UserRole.candidate, userRoles)}
                    icon="job-experience"
                    paragraph={trans('containers.jobExperiencesCard.nullState.paragraph')}
                    subParagraph={trans('containers.jobExperiencesCard.nullState.subParagraph')}
                    buttonText={trans('containers.jobExperiencesCard.nullState.callToAction')}
                    onButtonClick={handleOpenDialog}
                    className="job-experiences-card__null-state"
                />
            )}

            {!isLoading && jobExperiences.length > 0 && (
                <ul className="job-experiences-card__job-wrapper">
                    {jobExperiences.map(jobExperience => (
                        <JobExperienceItem
                            key={jobExperience.id}
                            isLoading={jobExperienceIsLoading}
                            isSuccessful={jobExperienceCreateIsSuccessful || jobExperienceUpdateIsSuccessful}
                            error={jobExperienceError}
                            jobExperience={jobExperience}
                            isEditable={userHasRole(UserRole.candidate, userRoles)}
                            onEdit={onEditJobExperience}
                            onDelete={onDeleteJobExperience}
                            onIsSuccessfulReset={onIsSuccessfulReset}
                        />
                    ))}
                </ul>
            )}

            <Dialog
                ref={dialogRef}
                onClose={handleCloseDialog}
                className="job-experiences-card__dialog"
            >
                <DialogContent
                    step={formStep}
                    totalSteps={numberOfFormSteps}
                    title={trans(`containers.jobExperiencesCard.dialog.${dialogStep}.title`)}
                    description={trans(`containers.jobExperiencesCard.dialog.${dialogStep}.description`)}
                >
                    {formStep === 1 && (
                        <JobExperienceForm
                            key={jobExperienceFormKey}
                            isLoading={jobExperienceIsLoading}
                            isSuccessful={jobExperienceCreateIsSuccessful || jobExperienceUpdateIsSuccessful}
                            occupationOptions={occupationOptions}
                            error={jobExperienceError}
                            onOccupationInput={onOccupationInput}
                            onSelectOccupation={onSelectOccupation}
                            onSubmit={handleAddJobExperience}
                            onCancel={handleCloseDialog}
                        />
                    )}

                    {formStep === 2 && (
                        <SkillsForm
                            isLoading={skillsIsLoading}
                            showSearch={skillOptions.length === 0}
                            occupation={occupation}
                            activeSkills={skills}
                            skillOptions={skillOptions}
                            searchedSkillOptions={searchedSkillOptions}
                            error={skillOptionsError}
                            onSkillSearchChange={onSearchSkills}
                            onSubmit={onAddSkills}
                            onCancel={handleCloseDialog}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </AddingCard>
    );
};

export default JobExperiencesCard;
